import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { ShopifyProduct } from '@/services/shopify';

interface WishlistItem {
  id: string;
  product_id: string;
  product_handle: string;
  product_data: ShopifyProduct;
  created_at: string;
}

interface WishlistStore {
  items: WishlistItem[];
  isLoading: boolean;
  isInitialized: boolean;
  
  // Actions
  fetchWishlist: () => Promise<void>;
  addToWishlist: (product: ShopifyProduct) => Promise<void>;
  removeFromWishlist: (productId: string) => Promise<void>;
  isInWishlist: (productId: string) => boolean;
  clearWishlist: () => void;
}

export const useWishlistStore = create<WishlistStore>((set, get) => ({
  items: [],
  isLoading: false,
  isInitialized: false,

  fetchWishlist: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      set({ items: [], isInitialized: true });
      return;
    }

    set({ isLoading: true });
    
    try {
      const { data, error } = await supabase
        .from('wishlist_items')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Cast the product_data from Json to ShopifyProduct
      const items: WishlistItem[] = (data || []).map(item => ({
        ...item,
        product_data: item.product_data as unknown as ShopifyProduct
      }));

      set({ items, isInitialized: true });
    } catch (error) {
      console.error('Error fetching wishlist:', error);
      set({ items: [], isInitialized: true });
    } finally {
      set({ isLoading: false });
    }
  },

  addToWishlist: async (product: ShopifyProduct) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in to add to wishlist');
    }

    set({ isLoading: true });
    
    try {
      const insertData = {
        user_id: user.id,
        product_id: product.node.id,
        product_handle: product.node.handle,
        product_data: product as any,
      };

      const { data, error } = await supabase
        .from('wishlist_items')
        .insert([insertData])
        .select()
        .single();

      if (error) throw error;

      // Cast the returned data
      const newItem: WishlistItem = {
        ...data,
        product_data: data.product_data as unknown as ShopifyProduct
      };

      set(state => ({
        items: [newItem, ...state.items],
      }));
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  removeFromWishlist: async (productId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('User must be logged in');
    }

    set({ isLoading: true });
    
    try {
      const { error } = await supabase
        .from('wishlist_items')
        .delete()
        .eq('user_id', user.id)
        .eq('product_id', productId);

      if (error) throw error;

      set(state => ({
        items: state.items.filter(item => item.product_id !== productId),
      }));
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  isInWishlist: (productId: string) => {
    return get().items.some(item => item.product_id === productId);
  },

  clearWishlist: () => {
    set({ items: [], isInitialized: false });
  },
}));
